import CatalogEntity from "../Components/CatalogEntity";
import {useEffect, useState} from "react";
import {SelectFieldCellEditor, renderColumnValue, findEnumLabelByValue} from "../../../General/SharedMethods";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, TextField} from "@mui/material";
import {MRT_EditActionButtons} from "material-react-table";
import * as yup from 'yup';

const ValveEditor = ({valveTypeOptions, enums}) => {
    const {getAll} = useHardwareCatalog();
    const [manufacturerOptions, setManufacturerOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const ViewValveDialog = (columns, viewRow, setViewRow) => {
        const components = [];
        for (let i = 0; i < columns.length; i += 3) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(3)].map((_, j) => {
                        const column = columns[i + j];
                        return column && (
                            <Grid item xs={12} sm={6} md={4} key={column.accessorKey}>
                                <TextField
                                    label={column.header}
                                    defaultValue={renderColumnValue(column, viewRow, enums)}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    variant="standard"
                                    fullWidth
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog maxWidth="md" open={true}>
                <DialogTitle variant="h4">View Details</DialogTitle>
                <DialogContent>
                    {components}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ValveDialog = (table, row, internalEditComponents, validationErrors, setValidationErrors, dialogType) => {
        const components = [];
        for (let i = 1; i < internalEditComponents.length; i += 2) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(2)].map((_, j) => {
                        const component = internalEditComponents[i + j];
                        return component && (
                            <Grid item xs={12} sm={6} key={`item-${i}-${j}`}>
                                {component}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={true}
            >
                <DialogTitle variant="h4">{dialogType === "edit" ? "Edit Valve" : "Create a new Valve"}</DialogTitle>
                <DialogContent
                    sx={{
                        overflowY: 'auto',
                    }}
                >
                    {components}
                </DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row}/>
                </DialogActions>
            </Dialog>
        );
    }


    useEffect(() => {
        getAll("manufacturers")
            .then(response => {
                setManufacturerOptions(response.data.content?.map(manufacturer => ({label: manufacturer.name, value: manufacturer})));
                setIsLoading(false);
            }).catch(error => {
            setIsLoading(false);
            console.error(error);
        })

    }, []);

    const createValveColumns = (validationErrors, setValidationErrors) => {
        return [
            {
                accessorKey: 'id',
                header: 'Id',
                enableEditing: false,
                muiEditTextFieldProps: {
                    type: 'select',
                    required: true,
                    error: !!validationErrors?.id,
                    helperText: validationErrors?.id,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            id: undefined,
                        }),
                },
            },
            {
                accessorKey: 'name',
                header: 'Name',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                },
            },
            {
                accessorKey: 'valveType',
                header: 'Valve Type',
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                editVariant: 'select',
                filterVariant: 'multi-select',
                filterSelectOptions: valveTypeOptions.map(option => option.label),
                editSelectOptions: valveTypeOptions,
                muiEditTextFieldProps: {
                    type: 'multi-select',
                    required: true,
                    error: !!validationErrors?.valveType,
                    helperText: validationErrors?.valveType,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            valveType: '',
                        }),
                },
            },
            {
                accessorKey: 'manufacturer',
                header: 'Manufacturer',
                filterVariant: 'multi-select',
                filterSelectOptions: manufacturerOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    manufacturerOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'model',
                header: 'Model',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.model,
                    helperText: validationErrors?.model,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            model: undefined,
                        }),
                },
            },
            {
                accessorKey: 'diameter',
                header: 'Diameter',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.diameter,
                    helperText: validationErrors?.diameter,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            diameter: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxTemperature',
                header: 'Max Temperature',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxTemperature,
                    helperText: validationErrors?.maxTemperature,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxTemperature: undefined,
                        }),
                },
            },
        ]
    }

    const valveValidationSchema = yup.object().shape({
        name: yup.string().required('Name is required').max(255, 'Name must be no longer than 255 characters'),
        valveType: yup.string().required('Valve Type is required'),
        manufacturer: yup.object().typeError('Manufacturer is required').required('Manufacturer is required'),
        model: yup.string().required('Model is required').max(255, 'Model must be no longer than 255 characters'),
        diameter: yup.number().typeError('Diameter is required').required('Diameter is required').min(0.5).max(12),
        maxTemperature: yup.number().typeError('Max Temperature is required').required('Max Temperature is required').min(0).max(200),
    });

    return isLoading ? <LinearProgress/> :
        <CatalogEntity
            entityType={'valves'}
            entityName={'Valve'}
            createColumns={createValveColumns}
            validationSchema={valveValidationSchema}
            customEditDialog={ValveDialog}
            customCreateDialog={ValveDialog}
            customViewDialog={ViewValveDialog}
            enums={enums}
        />

}

export default ValveEditor;
